import React, { useState, createContext, useMemo, useCallback } from 'react';
import { useSearchResults, SearchedSpace } from './SearchResultsProvider';
import type {
  CapacityValues,
  DateTimeFilter,
} from '../../../types/components/filter_bar_types';
import {
  checkGroupSize,
  checkLiveAvailability,
  checkPriceCategory,
  checkSpaceType,
  checkAmenities,
  checkDateTimeFilter,
  defaultSpaceCategory,
  checkBounds,
} from '../utils/filter';

const FilteredSearchResultsContext = createContext<{
  filteredSpaces: SearchedSpace[];
  filteredVenues: SearchedSpace['venue'][];
  setCapacityFilterValues: (capacityFilterValues: CapacityValues) => void;
  setSelectedCategory: (category: string) => void;
  capacityFilterValues: CapacityValues;
  selectedCategory: string;
  hoveredSearchResult: { id: string } | null;
  setHoveredSearchResult: (searchResult: { id: string } | null) => void;
  liveAvailabilityFilter: boolean;
  setLiveAvailabilityFilter: (enabled: boolean) => void;
  amenityFilters: string[];
  setAmenityFilters: (amenityIds: string[]) => void;
  priceCategoryFilter: {
    affordable: boolean;
    mid_range: boolean;
    luxury: boolean;
  };
  setPriceCategoryFilter: (priceCategoryFilter: {
    affordable: boolean;
    mid_range: boolean;
    luxury: boolean;
  }) => void;
  dateTimeFilter: DateTimeFilter | null;
  setDateTimeFilter: (dateTimeFilter: DateTimeFilter | null) => void;
  getActiveFilterCount: () => number;
}>({
  filteredSpaces: [],
  filteredVenues: [],
  setCapacityFilterValues: () => {},
  setSelectedCategory: () => {},
  capacityFilterValues: {
    minGuests: undefined,
    maxGuests: undefined,
    allowSeated: false,
    allowStanding: false,
  },
  selectedCategory: defaultSpaceCategory,
  hoveredSearchResult: null,
  setHoveredSearchResult: () => {},
  liveAvailabilityFilter: false,
  setLiveAvailabilityFilter: () => {},
  amenityFilters: [],
  setAmenityFilters: () => {},
  priceCategoryFilter: {
    affordable: false,
    mid_range: false,
    luxury: false,
  },
  setPriceCategoryFilter: () => {},
  dateTimeFilter: null,
  setDateTimeFilter: () => {},
  getActiveFilterCount: () => 0,
});
export const FilteredSearchResultsProvider = ({
  children,
}: {
  children: JSX.Element[] | JSX.Element;
}) => {
  const { spaces, currentBounds: bounds } = useSearchResults();
  const [capacityFilterValues, setCapacityFilterValues] =
    useState<CapacityValues>({
      minGuests: undefined,
      maxGuests: undefined,
      allowSeated: false,
      allowStanding: false,
    });
  const [selectedCategory, setSelectedCategory] =
    useState<string>(defaultSpaceCategory);
  const [hoveredSearchResult, setHoveredSearchResult] = useState<{
    id: string;
  } | null>(null);
  const [liveAvailabilityFilter, setLiveAvailabilityFilter] =
    useState<boolean>(false);
  const [amenityFilters, setAmenityFilters] = useState<string[]>([]);
  const [priceCategoryFilter, setPriceCategoryFilter] = useState<{
    affordable: boolean;
    mid_range: boolean;
    luxury: boolean;
  }>({
    affordable: false,
    mid_range: false,
    luxury: false,
  });
  const [dateTimeFilter, setDateTimeFilter] = useState<DateTimeFilter | null>(
    null,
  );

  const filteredSpaces = useMemo(() => {
    const filterSpace = (space: SearchedSpace) => {
      const isBigEnough = checkGroupSize(space, capacityFilterValues);

      const fitsCategory = checkSpaceType({
        category: selectedCategory,
        spaceType: space.type || 'None',
        venueType: space.venue.type || 'None',
      });

      const meetsLiveAvailability = checkLiveAvailability(
        space,
        liveAvailabilityFilter,
      );

      const isInBounds = checkBounds(space, bounds);

      const meetsPriceCategory = checkPriceCategory(space, priceCategoryFilter);

      const meetsAmenityFilters = checkAmenities(space, amenityFilters);

      const meetsDateTimeFilter = checkDateTimeFilter(space, dateTimeFilter);

      return (
        isBigEnough &&
        fitsCategory &&
        meetsLiveAvailability &&
        isInBounds &&
        meetsPriceCategory &&
        meetsAmenityFilters &&
        meetsDateTimeFilter
      );
    };

    return spaces.filter((space) => filterSpace(space));
  }, [
    spaces,
    capacityFilterValues,
    selectedCategory,
    liveAvailabilityFilter,
    amenityFilters,
    bounds,
    priceCategoryFilter,
    dateTimeFilter,
  ]);

  const filteredVenues = useMemo(() => {
    const venuesById = filteredSpaces.reduce<
      Record<string, SearchedSpace['venue']>
    >((acc, space) => {
      acc[space.venue.slug || ''] = space.venue;
      return acc;
    }, {});

    return Object.values(venuesById);
  }, [filteredSpaces]);

  const getActiveFilterCount = useCallback(() => {
    let filterCount = 0;
    if (liveAvailabilityFilter) {
      filterCount++;
    }

    if (amenityFilters.length > 0) {
      filterCount += amenityFilters.length;
    }

    if (Object.values(priceCategoryFilter).some((filter) => filter)) {
      filterCount++;
    }

    return filterCount;
  }, [liveAvailabilityFilter, amenityFilters, priceCategoryFilter]);

  return (
    <FilteredSearchResultsContext.Provider
      value={{
        filteredSpaces,
        filteredVenues,
        setSelectedCategory,
        capacityFilterValues,
        setCapacityFilterValues,
        selectedCategory,
        hoveredSearchResult,
        setHoveredSearchResult,
        liveAvailabilityFilter,
        setLiveAvailabilityFilter,
        amenityFilters,
        setAmenityFilters,
        priceCategoryFilter,
        setPriceCategoryFilter,
        dateTimeFilter,
        setDateTimeFilter,
        getActiveFilterCount,
      }}
    >
      {children}
    </FilteredSearchResultsContext.Provider>
  );
};

export const useFilteredSearchResults = () =>
  React.useContext(FilteredSearchResultsContext);
